import { AnyOverlay } from '../../models/Post';
import { getBackdropExtension } from './backdropExtension';

/** Adapt layout boxes without stretching foreground artwork or changing saved data. */
export function adaptVideoOverlay(
  overlay: AnyOverlay,
  sourceWidth: number,
  sourceHeight: number,
  outputWidth: number,
  outputHeight: number,
  referenceWidth = sourceWidth,
  overlays: AnyOverlay[] = [],
  layoutEngine?: 'fabric' | 'remotion',
): AnyOverlay {
  if (!overlay.type || overlay.type === 'text') return overlay;

  if (!overlay.width) return overlay;
  const originalWidth = overlay.width / 100 * sourceWidth;
  const originalHeight = (overlay.height ?? 0) / 100 * sourceHeight;
  const isRoundImage = overlay.type === 'image' && originalHeight > 0 &&
    (overlay.borderRadius ?? 0) * sourceWidth / referenceWidth >= Math.min(originalWidth, originalHeight) / 2;
  // Large SVGs behind text are layout surfaces (cards, speech bubbles, frames).
  // Keep their percentage geometry with their content instead of independently
  // shrinking and recentering them like foreground icons.
  const isTextBackdrop = overlay.type === 'svg' &&
    (overlay.width > 25 || (overlay.height ?? 0) > 25) && overlays.some(other =>
      (!other.type || other.type === 'text') &&
      other.position.x < overlay.position.x + overlay.width! &&
      other.position.x + (other.width ?? 0) > overlay.position.x &&
      other.position.y < overlay.position.y + (overlay.height ?? 0) &&
      other.position.y + (other.height ?? 100 - other.position.y) > overlay.position.y
    );
  // Rectangles, rules and dimension-aware SVGs describe layout, not icons.
  // Paint does not determine layout: gradient icons must keep their shape.
  // Full-canvas surfaces are identified by coverage, just like backdrop bands.
  const responsiveSvg = overlay.type === 'svg' && (
    /__(?:W|H|R)__/.test(overlay.svgContent) ||
    (/<(?:rect|line)\b/i.test(overlay.svgContent) && !/<(?:path|circle|ellipse)\b/i.test(overlay.svgContent)) ||
    (overlay.position.x <= 3 && overlay.position.y <= 3 &&
      overlay.position.x + overlay.width >= 97 &&
      overlay.position.y + (overlay.height ?? 0) >= 97) ||
    isTextBackdrop || !!getBackdropExtension(overlay, sourceWidth, sourceHeight)
  );
  const templateImage = overlay.type === 'image' && (overlay.isTemplateAsset || overlay.id === 'notes-page') && !isRoundImage;
  // notes-page is the legacy template ID, before provenance was persisted.
  const adapted = templateImage && overlay.objectFit !== 'contain' ? { ...overlay, objectFit: 'fill' as const } : overlay;
  const preserveShape = overlay.type === 'svg' && !responsiveSvg;
  if ((!preserveShape && !isRoundImage) || originalHeight <= 0) {
    // A quarter-turned layer's stored width controls its visible height.
    // Map the visible rectangle, then convert back to pre-rotation geometry.
    const rotation = ((overlay.rotation ?? 0) % 360 + 360) % 360;
    if (originalHeight > 0 && (Math.abs(rotation - 90) < 0.01 || Math.abs(rotation - 270) < 0.01)) {
      const sx = outputWidth / sourceWidth;
      const sy = outputHeight / sourceHeight;
      const w = originalWidth * sy;
      const h = originalHeight * sx;
      const width = w / outputWidth * 100;
      const height = h / outputHeight * 100;
      if (layoutEngine !== 'remotion') {
        // Fabric compensation rotates about the stored top-left anchor.
        return { ...adapted, width, height };
      }
      return { ...adapted, width, height, position: {
        x: overlay.position.x + (overlay.width - width) / 2,
        y: overlay.position.y + ((overlay.height ?? height) - height) / 2,
      } };
    }
    return adapted;
  }

  // Fit uniformly inside the new percentage box and retain its center.
  // This also preserves layered circular badges and the icons inside them.
  const scale = Math.min(outputWidth / sourceWidth, outputHeight / sourceHeight);
  const width = originalWidth * scale / outputWidth * 100;
  const height = originalHeight * scale / outputHeight * 100;
  return {
    ...overlay,
    width,
    height,
    position: {
      x: overlay.position.x + (overlay.width - width) / 2,
      y: overlay.position.y + ((overlay.height ?? height) - height) / 2,
    },
  };
}
